import { API_BASE_URL } from "../constants";
import { Scenario } from "../types";

/**
 * HTTP client for the scenario-authoring endpoints. Mirrors the conventions in
 * `apiUtil.ts`: `Bearer ${token}` auth, JSON content type, throws on
 * any non-2xx response.
 *
 * SECURITY NOTE: This module NEVER constructs an `objectKey` for an upload.
 * Object keys are minted server-side from validated inputs; we just round-
 * trip the value the server returns back into the create/update calls.
 */

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MiB — match server cap

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

async function jsonOrThrow<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    let detail = "";
    try {
      const body = await response.json();
      detail =
        typeof body?.message === "string"
          ? `: ${body.message}`
          : Array.isArray(body?.message)
            ? `: ${body.message.join(", ")}`
            : "";
    } catch {
      // ignore — body wasn't JSON
    }
    throw new Error(`${action} failed: ${response.status}${detail}`);
  }
  // Some endpoints return 204 No Content.
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

// ------------------------------------------------------------------
// Scenario drafts
// ------------------------------------------------------------------

export async function createScenarioDraft(
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE_URL}/scenarios`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });
  return jsonOrThrow(response, "Create draft scenario");
}

export interface ScenarioPatch {
  title?: string;
  // exactly one of categoryId / categoryName — never both
  categoryId?: string;
  categoryName?: string;
  description?: string;
  instructions?: string;
  logoObjectKey?: string;
  heroObjectKey?: string;
  playerRole?: string;
  objective?: string;
  briefing?: string;
  // Writable only by an admin — sending this as a manager gets a 403 from the
  // server. Callers should only include it when usePermissionLevel().isAdmin.
  isGlobal?: boolean;
}

export async function updateScenarioDraft(
  id: string,
  patch: ScenarioPatch,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE_URL}/scenarios/${id}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return jsonOrThrow(response, "Update draft scenario");
}

export async function publishScenario(
  id: string,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE_URL}/scenarios/${id}/publish`, {
    method: "POST",
    headers: authHeaders(token),
  });
  return jsonOrThrow(response, "Publish scenario");
}

export async function deleteScenarioDraft(
  id: string,
  token?: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/scenarios/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  await jsonOrThrow<void>(response, "Delete draft scenario");
}

export type ScenarioDraftSummary = Pick<
  Scenario,
  "id" | "title" | "description" | "category"
> & { canEdit: boolean; status?: "draft" | "published"; hero?: string };

export async function listMyDrafts(
  token?: string,
): Promise<ScenarioDraftSummary[]> {
  const response = await fetch(`${API_BASE_URL}/scenarios?status=draft`, {
    headers: authHeaders(token),
  });
  return jsonOrThrow(response, "List my drafts");
}

export async function listCategories(
  token?: string,
): Promise<{ id: string; name: string }[]> {
  const response = await fetch(`${API_BASE_URL}/scenarios/categories`, {
    headers: authHeaders(token),
  });
  return jsonOrThrow(response, "List categories");
}

export interface ScenarioOption {
  id: string;
  title: string;
}

export async function listScenarioOptions(
  token?: string,
): Promise<ScenarioOption[]> {
  const res = await fetch(`${API_BASE_URL}/scenarios`, {
    headers: authHeaders(token),
  });
  return jsonOrThrow<ScenarioOption[]>(res, "List scenarios");
}

// ------------------------------------------------------------------
// Personas (nested)
// ------------------------------------------------------------------

export interface PersonaPayload {
  name: string;
  role: string;
  avatarObjectKey?: string;
  description?: string;
  bio?: string;
  // null clears the persona's voice back to the deployment default; undefined
  // (on a PATCH) leaves whatever is stored alone.
  voiceId?: string | null;
  instructions: string;
  accessibleDocumentIds: string[];
}

export async function createPersona(
  scenarioId: string,
  persona: PersonaPayload,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/personas`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(persona),
    },
  );
  return jsonOrThrow(response, "Create persona");
}

export async function updatePersona(
  scenarioId: string,
  personaId: string,
  persona: Partial<PersonaPayload>,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/personas/${personaId}`,
    {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(persona),
    },
  );
  return jsonOrThrow(response, "Update persona");
}

export async function deletePersona(
  scenarioId: string,
  personaId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/personas/${personaId}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    },
  );
  await jsonOrThrow<void>(response, "Delete persona");
}

// ------------------------------------------------------------------
// Documents (nested)
// ------------------------------------------------------------------

export interface DocumentPayload {
  name: string;
  description: string;
  instructions?: string;
  objectKey: string;
}

export async function createDocument(
  scenarioId: string,
  doc: DocumentPayload,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/documents`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(doc),
    },
  );
  return jsonOrThrow(response, "Create document");
}

export async function updateDocument(
  scenarioId: string,
  documentId: string,
  doc: Partial<DocumentPayload>,
  token?: string,
): Promise<{ id: string }> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/documents/${documentId}`,
    {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(doc),
    },
  );
  return jsonOrThrow(response, "Update document");
}

export async function deleteDocument(
  scenarioId: string,
  documentId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/scenarios/${scenarioId}/documents/${documentId}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    },
  );
  await jsonOrThrow<void>(response, "Delete document");
}

// ------------------------------------------------------------------
// Uploads
// ------------------------------------------------------------------

export type UploadKind =
  | "scenario-logo"
  | "scenario-hero"
  | "persona-avatar"
  | "document";

export interface SignUploadBody {
  kind: UploadKind;
  scenarioId: string;
  personaId?: string;
  documentId?: string;
  filename: string;
  contentType: string;
}

export interface SignUploadResult {
  objectKey: string;
  signedUrl: string;
  expiresAt: number;
}

export async function signUpload(
  body: SignUploadBody,
  token?: string,
): Promise<SignUploadResult> {
  const response = await fetch(`${API_BASE_URL}/scenarios/uploads/sign`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return jsonOrThrow(response, "Sign upload URL");
}

/**
 * PUT the file bytes to GCS using the signed URL. The server signs both
 * `Content-Type` and `x-goog-content-length-range` into the URL, so the
 * client MUST send both with values that exactly match the signature.
 * The length-range value here must stay in sync with MAX_UPLOAD_BYTES in
 * server/src/scenarios/gcs.service.ts.
 */
export async function uploadToSignedUrl(
  signedUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "x-goog-content-length-range": "0,10485760",
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(
      `Upload to GCS failed: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Convenience: validate the size client-side, mint the signed URL, PUT the
 * file, and return the `objectKey`. The caller then patches/creates the
 * appropriate entity with that key.
 */
export async function uploadAsset(
  kind: UploadKind,
  ids: { scenarioId: string; personaId?: string; documentId?: string },
  file: File,
  token?: string,
): Promise<string> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `File is larger than 10 MB (got ${(file.size / 1024 / 1024).toFixed(1)} MB).`,
    );
  }
  const { objectKey, signedUrl } = await signUpload(
    {
      kind,
      scenarioId: ids.scenarioId,
      personaId: ids.personaId,
      documentId: ids.documentId,
      filename: file.name,
      contentType: file.type || "application/octet-stream",
    },
    token,
  );
  await uploadToSignedUrl(signedUrl, file);
  return objectKey;
}
